import { FunctionComponent, useCallback, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import {
  EventActivityStatus,
  EventStatus,
  EventUserStatus,
  organisationApi,
} from '../../../services';
import {
  cookieOptions,
  EVENTS_FEED_ADMIN_COOKIE_KEY,
  EVENTS_FEED_COOKIE_KEY,
  Filter,
  FilterValue,
  isAdminLayout,
  optArrItem,
  useAppDispatch,
  useAuthorsFilter,
  useDateRangeFilter,
  useFilter,
} from '../../../shared';

import classes from './EventsFeedRightSide.module.scss';

export const EventsFeedRightSide: FunctionComponent = () => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'events' });

  const dispatch = useAppDispatch();

  const { getGroupValue, getDateRangeValue } = useFilter();

  const cookieKey = isAdminLayout() ? EVENTS_FEED_ADMIN_COOKIE_KEY : EVENTS_FEED_COOKIE_KEY;

  const [cookies, setCookie] = useCookies([cookieKey]);

  const eventsFeedCookies = useMemo(() => {
    return { ...cookies[cookieKey] };
  }, [cookieKey, cookies]);

  const filter = useMemo<FilterValue[]>(() => eventsFeedCookies?.filter ?? [], [eventsFeedCookies]);

  const statusesGroup = useMemo(() => {
    return {
      id: 'statuses',
      title: t('common.status'),
      items: [
        ...optArrItem(isAdminLayout(), {
          id: EventStatus.PUBLISHED,
          label: t('common.published'),
          value: EventStatus.PUBLISHED,
        }),
        ...optArrItem(isAdminLayout(), {
          id: EventStatus.UNPUBLISHED,
          label: t('common.unpublished'),
          value: EventStatus.UNPUBLISHED,
        }),
        {
          id: EventStatus.CANCELLED,
          label: ctxT('include-cancelled'),
          value: EventStatus.CANCELLED,
        },
      ],
      multiCheck: true,
      allowReset: true,
      allowCollapse: false,
    };
  }, [ctxT, t]);

  const activityStatusesGroup = useMemo(() => {
    return {
      id: 'activityStatuses',
      title: t('common.activity-status'),
      items: [
        {
          id: EventActivityStatus.PAST,
          label: ctxT('include-past'),
          value: EventActivityStatus.PAST,
        },
      ],
      multiCheck: true,
      allowReset: true,
      allowCollapse: false,
    };
  }, [ctxT, t]);

  const participantStatusesGroup = useMemo(() => {
    return {
      id: 'participantStatuses',
      title: ctxT('participant-status'),
      items: Object.keys(EventUserStatus)
        .filter((status) => status !== EventUserStatus.HOST)
        .map((status) => ({
          id: status,
          label: ctxT(`participant-status-${status.toLowerCase()}`),
          value: status,
        })),
      multiCheck: true,
      allowReset: true,
      allowCollapse: false,
    };
  }, [ctxT]);

  const { group: dateRangeGroup } = useDateRangeFilter({
    filter,
    showPastDates: false,
    titleLocaliseKey: 'common.time-range',
  });

  const fetchHosts = useCallback(
    ({ query, page }: { query: string; page?: number }) => {
      return dispatch(
        organisationApi.endpoints.organisationUsers.initiate({ page, filters: { query } })
      );
    },
    [dispatch]
  );

  const { group: hostsGroup } = useAuthorsFilter({
    filter,
    fetchItemsQuery: fetchHosts,
    groupId: 'hostIds',
    titleLocaliseKey: 'events.hosts',
    placeholderLocaliseKey: 'events.hosts-placeholder',
  });

  const groups = useMemo(
    () => [
      statusesGroup,
      activityStatusesGroup,
      participantStatusesGroup,
      dateRangeGroup,
      hostsGroup,
    ],
    [activityStatusesGroup, dateRangeGroup, hostsGroup, participantStatusesGroup, statusesGroup]
  );

  const onChange = useCallback(
    (filter: FilterValue[]) => {
      const statuses = getGroupValue({
        filter,
        groupId: 'statuses',
        multiCheck: true,
      });

      const activityStatuses = getGroupValue({
        filter,
        groupId: 'activityStatuses',
        multiCheck: true,
      });

      const participantStatuses = getGroupValue({
        filter,
        groupId: 'participantStatuses',
        multiCheck: true,
      });

      const { dateRange } = getDateRangeValue({ filter, groupId: 'date' });

      const hostIds = getGroupValue({
        filter,
        multiCheck: true,
        propToMap: 'id',
        groupId: 'hostIds',
      });

      const requestFilters = {
        ...(statuses.length && { statuses }),
        ...(activityStatuses.length && { activityStatuses }),
        ...(participantStatuses.length && { participantStatuses }),
        ...(hostIds.length && { hostIds }),
        dateRange,
      };

      setCookie(cookieKey, { ...eventsFeedCookies, requestFilters, filter }, cookieOptions());
    },
    [cookieKey, eventsFeedCookies, getDateRangeValue, getGroupValue, setCookie]
  );

  return (
    <div className={classes['events-feed-right-side']}>
      <h2 className={classes['events-feed-right-side__title']}>{t('common.filter')}</h2>
      <Filter groups={groups} filter={filter} onChange={onChange} />
    </div>
  );
};
